// src/modules/livro-ordens/LivroOrdemPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { db, paths } from '../../firebase/config';
import { collection, query, orderBy, onSnapshot, deleteDoc, doc } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import OrderPostForm from './OrderPostForm';

// Ícones SVG Nativos
const SearchIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>;
const FilterIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"></path></svg>;
const PlusIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>;
const Trash2Icon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>;
const PencilIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg>;

// Ícones de Categoria
const InfoIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>;
const ClockIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>;
const AlertCircleIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>;
const CheckCircleIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>;

const CATEGORIES = [
  { value: 'info', label: '1ª Companhia', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: <InfoIcon /> },
  { value: 'ordem', label: '2ª Companhia', color: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: <ClockIcon /> },
  { value: 'urgente', label: '3ª Companhia', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: <AlertCircleIcon /> },
  { value: 'tarefa', label: '4ª Companhia', color: 'bg-red-50 text-red-700 border-red-200', icon: <CheckCircleIcon /> },
];

const formatarDataSimples = (dataString) => {
  if (!dataString) return '-';
  const partes = dataString.split('-');
  if (partes.length === 3) return `${partes[2]}/${partes[1]}/${partes[0]}`;
  return dataString;
};

export default function LivroOrdemPage() {
  const { userProfile, isAdmin, isGerenciador } = useAuth();
  const [ordens, setOrdens] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [isAdding, setIsAdding] = useState(false);
  const [ordemEditando, setOrdemEditando] = useState(null); // Estado da edição
  
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  const canPost = isAdmin || isGerenciador || userProfile?.canPostLivroDeOrdem === true;

  useEffect(() => {
    const q = query(collection(db, paths.livro_ordens), orderBy('criado_em', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setOrdens(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (error) => {
      console.error("Erro ao carregar o Livro de Ordens:", error);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleAddNova = () => {
    setOrdemEditando(null);
    setIsAdding(true);
  };

  const handleEdit = (ordem) => {
    setOrdemEditando(ordem);
    setIsAdding(true);
  };

  const deleteItem = async (id) => {
    if (window.confirm("Deseja excluir esta publicação permanentemente?")) {
      try {
        await deleteDoc(doc(db, paths.livro_ordens, id));
      } catch (err) {
        console.error("Erro ao excluir ordem:", err);
      }
    }
  };

  const filteredItems = useMemo(() => {
    return ordens.filter(item => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = 
        (item.numeracao || item.titulo || '').toLowerCase().includes(searchLower) || 
        (item.conteudo || '').toLowerCase().includes(searchLower) ||
        (item.emanada_por || '').toLowerCase().includes(searchLower);
      
      const itemCat = item.categoria || 'ordem';
      const matchesCategory = filterCategory === 'all' || itemCat === filterCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [ordens, searchQuery, filterCategory]);

  const formatDateTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  };

  // Se for Admin/Gerenciador OU for autor de alguma ordem exibida, a coluna "Ações" aparece
  const showActionsColumn = isAdmin || isGerenciador || filteredItems.some(item => userProfile?.id === item.autor_id);

  if (loading) return <div className="p-8 text-center text-slate-500 font-medium">Carregando mural...</div>;

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-slate-800 font-sans p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header Compacto */}
        <header className="flex flex-col md:flex-row md:items-center justify-between mb-5 gap-3 animate-fade-in">
          <div className="flex items-center gap-4">
            <img src="https://apmbb.com.br/resultados/brasao.png" alt="Brasão APMBB" className="h-12 w-12" />
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 leading-tight">Livro de Ordens</h1>
              <p className="text-slate-500 text-xs font-medium">Gestão centralizada de ordens e avisos.</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {canPost && (
              <button 
                onClick={handleAddNova}
                className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-bold text-sm transition-all shadow-sm active:scale-95"
              >
                <PlusIcon />
                Nova Ordem
              </button>
            )}
          </div>
        </header>

        {/* Filters & Search */}
        <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm mb-5 flex flex-col md:flex-row gap-3 items-center animate-fade-in">
          <div className="relative flex-1 w-full text-slate-400">
            <div className="absolute left-3 top-1/2 -translate-y-1/2"><SearchIcon /></div>
            <input 
              type="text" 
              placeholder="Pesquisar por numeração, emissor ou conteúdo..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm font-medium text-slate-700"
            />
          </div>
          
          <div className="flex items-center gap-1.5 w-full md:w-auto overflow-x-auto pb-1 md:pb-0 no-scrollbar">
            <div className="text-slate-400 shrink-0 ml-1 mr-1"><FilterIcon /></div>
            <button 
              onClick={() => setFilterCategory('all')}
              className={`px-3 py-1 rounded-full text-xs font-bold transition-all whitespace-nowrap ${
                filterCategory === 'all' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Todos
            </button>
            {CATEGORIES.map(cat => (
              <button 
                key={cat.value}
                onClick={() => setFilterCategory(cat.value)}
                className={`px-2.5 py-1 rounded-full text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1 ${
                  filterCategory === cat.value ? 'ring-1 ring-offset-1 ring-current ' + cat.color.split(' ')[0] + ' ' + cat.color.split(' ')[1] : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {cat.icon}
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content em Formato de Tabela */}
        {filteredItems.length > 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-x-auto animate-fade-in">
            <table className="w-full text-left border-collapse min-w-[900px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="p-3 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider w-24">Ordem Nº</th>
                  <th className="p-3 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider w-28">Categoria</th>
                  <th className="p-3 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider w-40">Origem / Transmissão</th>
                  <th className="p-3 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider w-36">Datas / Local</th>
                  <th className="p-3 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider min-w-[250px]">Mensagem / Conteúdo</th>
                  <th className="p-3 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider w-32">Publicação</th>
                  
                  {/* Cabeçalho de Ações renderizado apenas para quem tem permissão */}
                  {showActionsColumn && (
                    <th className="p-3 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider text-center w-24">Ações</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredItems.map((item, index) => {
                  const itemCat = item.categoria || 'ordem';
                  const categoryInfo = CATEGORIES.find(c => c.value === itemCat) || CATEGORIES[1];
                  
                  // Verifica se o usuário tem permissão sobre ESTA linha específica
                  const canAction = isAdmin || isGerenciador || userProfile?.id === item.autor_id;

                  return (
                    <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                      
                      {/* Numeração */}
                      <td className="p-3 align-top">
                        <span className="font-extrabold text-slate-900 text-sm uppercase">
                          {item.numeracao || item.titulo || '-'}
                        </span>
                      </td>

                      {/* Categoria */}
                      <td className="p-3 align-top">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border inline-flex items-center gap-1 ${categoryInfo.color}`}>
                          {categoryInfo.icon}
                          {categoryInfo.label}
                        </span>
                      </td>

                      {/* Origem e Transmissão */}
                      <td className="p-3 align-top">
                        <div className="mb-1">
                          <span className="text-[9px] font-bold text-slate-400 block uppercase">Emanada Por</span>
                          <span className="text-xs font-bold text-slate-700 uppercase">{item.emanada_por || '-'}</span>
                        </div>
                        <div>
                          <span className="text-[9px] font-bold text-slate-400 block uppercase">Transmitida Por</span>
                          <span className="text-xs font-semibold text-slate-600 uppercase">{item.transmitida_por || '-'}</span>
                        </div>
                      </td>

                      {/* Datas e Local */}
                      <td className="p-3 align-top">
                        <div className="mb-1">
                          <span className="text-[9px] font-bold text-slate-400 uppercase mr-1">Data:</span>
                          <span className="text-xs font-bold text-slate-700">{formatarDataSimples(item.data_ordem)}</span>
                        </div>
                        <div className="mb-1">
                          <span className="text-[9px] font-bold text-slate-400 uppercase mr-1">Val.:</span>
                          <span className="text-xs font-semibold text-slate-600 uppercase">{item.validade || '-'}</span>
                        </div>
                        <div>
                          <span className="text-[9px] font-bold text-slate-400 block uppercase">Local/Meio</span>
                          <span className="text-xs font-semibold text-slate-600 uppercase">{item.local_meio || '-'}</span>
                        </div>
                      </td>

                      {/* Mensagem / Conteúdo */}
                      <td className="p-3 align-top max-w-sm">
                        <p className="text-xs text-slate-700 leading-snug whitespace-pre-wrap break-words">
                          {item.conteudo}
                        </p>
                      </td>

                      {/* Publicação */}
                      <td className="p-3 align-top">
                        <div className="flex flex-col">
                          <span className="text-[10px] font-extrabold text-blue-600 uppercase mb-0.5">
                            CAD PM {item.autor_nome || 'Autor'}
                          </span>
                          <span className="text-[9px] font-bold text-slate-400">
                            {formatDateTimestamp(item.criado_em)}
                          </span>
                        </div>
                      </td>

                      {/* Célula de Ações (Editar / Excluir) */}
                      {showActionsColumn && (
                        <td className="p-3 align-top text-center">
                          {canAction && (
                            <div className="flex justify-center items-center gap-1">
                              <button 
                                onClick={() => handleEdit(item)}
                                className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                                title="Editar Ordem"
                              >
                                <PencilIcon />
                              </button>
                              <button 
                                onClick={() => deleteItem(item.id)}
                                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                                title="Excluir Ordem"
                              >
                                <Trash2Icon />
                              </button>
                            </div>
                          )}
                        </td>
                      )}

                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400 animate-fade-in bg-white rounded-xl border border-slate-200 border-dashed">
            <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mb-3 text-slate-300">
              <SearchIcon />
            </div>
            <p className="text-base font-bold text-slate-600">Nenhum item encontrado</p>
            <p className="text-xs font-medium">O mural está vazio ou sua busca não retornou resultados.</p>
          </div>
        )}

        {/* Modal de Formulário (Nova/Editar) */}
        <OrderPostForm 
          isOpen={isAdding} 
          onClose={() => { setIsAdding(false); setOrdemEditando(null); }} 
          ordemEditando={ordemEditando} 
        />
        
      </div>
    </div>
  );
}